import sodium from "libsodium-wrappers-sumo";
import type { DeviceRegistrationPayload, RoomKeyStore } from "./types";

type SerializedRoomKey = Readonly<{
  chatId: string;
  epoch: number;
  key: string;
}>;

type KeyTransferPayload = Readonly<{
  createdAt: string;
  keys: SerializedRoomKey[];
  username: string;
  version: 1;
}>;

export type EncryptedDeviceLink = Readonly<{
  claimPublicKey: string;
  ciphertext: string;
  nonce: string;
  secret: string;
}>;

type RecoveryDocument = Readonly<{
  ciphertext: string;
  format: "jtuc-live-recovery";
  nonce: string;
  salt: string;
  version: 1;
}>;

const RECOVERY_AAD = "JTUC-LIVE-RECOVERY-V1";
const DEVICE_LINK_AAD = "JTUC-LIVE-DEVICE-LINK-V1";
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function deviceLinkClaimText(linkId: string, device: DeviceRegistrationPayload): string {
  return JSON.stringify({
    v: 1,
    linkId,
    device: {
      id: device.id,
      name: device.name,
      platform: device.platform,
      encryptionPublicKey: device.encryptionPublicKey,
      signingPublicKey: device.signingPublicKey,
    },
  });
}

function deviceLinkClaimMessage(linkId: string, device: DeviceRegistrationPayload): Uint8Array {
  return sodium.from_string(deviceLinkClaimText(linkId, device));
}

function encode(value: Uint8Array): string {
  return sodium.to_base64(value, sodium.base64_variants.URLSAFE_NO_PADDING);
}

function decode(value: string): Uint8Array {
  return sodium.from_base64(value, sodium.base64_variants.URLSAFE_NO_PADDING);
}

function deviceLinkProofSeed(secret: Uint8Array): Uint8Array {
  return sodium.crypto_generichash(
    sodium.crypto_sign_SEEDBYTES,
    sodium.from_string("JTUC-LIVE-DEVICE-LINK-PROOF-V1"),
    secret,
  );
}

function normalizeUsername(value: string): string {
  return value.trim().toLowerCase();
}

function serializeKeys(keys: RoomKeyStore): SerializedRoomKey[] {
  const result: SerializedRoomKey[] = [];
  for (const [chatId, epochs] of keys) {
    for (const [epoch, key] of epochs) {
      result.push({ chatId, epoch, key: encode(key) });
    }
  }
  return result.sort((left, right) => left.chatId.localeCompare(right.chatId) || left.epoch - right.epoch);
}

function payloadBytes(keys: RoomKeyStore, username: string): Uint8Array {
  const payload: KeyTransferPayload = {
    version: 1,
    username: normalizeUsername(username),
    createdAt: new Date().toISOString(),
    keys: serializeKeys(keys),
  };
  return sodium.from_string(JSON.stringify(payload));
}

function parsePayload(value: Uint8Array, expectedUsername: string): KeyTransferPayload {
  let parsed: unknown;
  try {
    parsed = JSON.parse(sodium.to_string(value));
  } catch {
    throw new Error("Резервная копия повреждена");
  }
  if (!parsed || typeof parsed !== "object") throw new Error("Некорректная резервная копия");
  const candidate = parsed as Partial<KeyTransferPayload>;
  if (candidate.version !== 1 || normalizeUsername(candidate.username ?? "") !== normalizeUsername(expectedUsername) || !Array.isArray(candidate.keys)) {
    throw new Error("Резервная копия принадлежит другому аккаунту или имеет неизвестный формат");
  }
  if (candidate.keys.length > 5_000) throw new Error("В резервной копии слишком много ключей");
  const seen = new Set<string>();
  for (const item of candidate.keys) {
    if (!item || typeof item !== "object") throw new Error("Некорректная запись ключа");
    const key = item as Partial<SerializedRoomKey>;
    if (typeof key.chatId !== "string" || !UUID_PATTERN.test(key.chatId) || !Number.isInteger(key.epoch) || (key.epoch ?? 0) < 1 || typeof key.key !== "string") {
      throw new Error("Некорректная запись ключа");
    }
    const uniqueKey = `${key.chatId}:${key.epoch}`;
    if (seen.has(uniqueKey) || decode(key.key).length !== sodium.crypto_aead_xchacha20poly1305_ietf_KEYBYTES) {
      throw new Error("Некорректная или повторяющаяся запись ключа");
    }
    seen.add(uniqueKey);
  }
  return candidate as KeyTransferPayload;
}

async function deriveRecoveryKey(passphrase: string, salt: Uint8Array): Promise<Uint8Array> {
  await sodium.ready;
  return sodium.crypto_pwhash(
    sodium.crypto_aead_xchacha20poly1305_ietf_KEYBYTES,
    passphrase,
    salt,
    sodium.crypto_pwhash_OPSLIMIT_INTERACTIVE,
    sodium.crypto_pwhash_MEMLIMIT_INTERACTIVE,
    sodium.crypto_pwhash_ALG_ARGON2ID13,
  );
}

export function payloadToKeyStore(payload: KeyTransferPayload): RoomKeyStore {
  const result: RoomKeyStore = new Map();
  for (const item of payload.keys) {
    const epochs = result.get(item.chatId) ?? new Map<number, Uint8Array>();
    epochs.set(item.epoch, decode(item.key));
    result.set(item.chatId, epochs);
  }
  return result;
}

export function mergeRoomKeys(target: RoomKeyStore, incoming: RoomKeyStore): number {
  let imported = 0;
  for (const [chatId, epochs] of incoming) {
    const existing = target.get(chatId) ?? new Map<number, Uint8Array>();
    for (const [epoch, key] of epochs) {
      if (!existing.has(epoch)) imported += 1;
      existing.set(epoch, key);
    }
    target.set(chatId, existing);
  }
  return imported;
}

export async function createRecoveryDocument(keys: RoomKeyStore, username: string, passphrase: string): Promise<string> {
  await sodium.ready;
  if (passphrase.length < 12) throw new Error("Защитная фраза должна содержать не менее 12 символов");
  const salt = sodium.randombytes_buf(sodium.crypto_pwhash_SALTBYTES);
  const nonce = sodium.randombytes_buf(sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES);
  const key = await deriveRecoveryKey(passphrase, salt);
  try {
    const ciphertext = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(
      payloadBytes(keys, username),
      sodium.from_string(RECOVERY_AAD),
      null,
      nonce,
      key,
    );
    const document: RecoveryDocument = {
      format: "jtuc-live-recovery",
      version: 1,
      salt: encode(salt),
      nonce: encode(nonce),
      ciphertext: encode(ciphertext),
    };
    return JSON.stringify(document);
  } finally {
    key.fill(0);
  }
}

export async function openRecoveryDocument(documentText: string, username: string, passphrase: string): Promise<RoomKeyStore> {
  await sodium.ready;
  let document: RecoveryDocument;
  try {
    document = JSON.parse(documentText) as RecoveryDocument;
  } catch {
    throw new Error("Не удалось прочитать файл резервной копии");
  }
  if (document?.format !== "jtuc-live-recovery" || document.version !== 1) throw new Error("Неизвестный формат резервной копии");
  const key = await deriveRecoveryKey(passphrase, decode(document.salt));
  try {
    const plain = sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
      null,
      decode(document.ciphertext),
      sodium.from_string(RECOVERY_AAD),
      decode(document.nonce),
      key,
    );
    return payloadToKeyStore(parsePayload(plain, username));
  } catch (error) {
    if (error instanceof Error && error.message.startsWith("Резервная копия")) throw error;
    throw new Error("Неверная защитная фраза или резервная копия повреждена");
  } finally {
    key.fill(0);
  }
}

export async function createEncryptedDeviceLink(keys: RoomKeyStore, username: string, linkId: string): Promise<EncryptedDeviceLink> {
  await sodium.ready;
  const secretBytes = sodium.randombytes_buf(sodium.crypto_aead_xchacha20poly1305_ietf_KEYBYTES);
  const nonceBytes = sodium.randombytes_buf(sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES);
  const ciphertext = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(
    payloadBytes(keys, username),
    sodium.from_string(`${DEVICE_LINK_AAD}:${linkId}`),
    null,
    nonceBytes,
    secretBytes,
  );
  const proofKeys = sodium.crypto_sign_seed_keypair(deviceLinkProofSeed(secretBytes));
  return {
    claimPublicKey: encode(proofKeys.publicKey),
    ciphertext: encode(ciphertext),
    nonce: encode(nonceBytes),
    secret: encode(secretBytes),
  };
}

export async function createDeviceLinkClaimProof(
  secret: string,
  linkId: string,
  device: DeviceRegistrationPayload,
): Promise<string> {
  await sodium.ready;
  const secretBytes = decode(secret);
  if (secretBytes.length !== sodium.crypto_sign_SEEDBYTES) throw new Error("Некорректный секрет подключения");
  const proofKeys = sodium.crypto_sign_seed_keypair(deviceLinkProofSeed(secretBytes));
  return encode(sodium.crypto_sign_detached(deviceLinkClaimMessage(linkId, device), proofKeys.privateKey));
}

export async function openEncryptedDeviceLink(
  ciphertext: string,
  nonce: string,
  secret: string,
  linkId: string,
  username: string,
): Promise<RoomKeyStore> {
  await sodium.ready;
  try {
    const plain = sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
      null,
      decode(ciphertext),
      sodium.from_string(`${DEVICE_LINK_AAD}:${linkId}`),
      decode(nonce),
      decode(secret),
    );
    return payloadToKeyStore(parsePayload(plain, username));
  } catch (error) {
    if (error instanceof Error && (error.message.includes("аккаунт") || error.message.includes("ключ"))) throw error;
    throw new Error("Ссылка подключения недействительна или повреждена");
  }
}

export function buildDeviceLinkUrl(origin: string, linkId: string, secret: string): string {
  const url = new URL(origin);
  url.hash = new URLSearchParams({ deviceLink: linkId, secret }).toString();
  return url.toString();
}

export function readDeviceLink(locationHash: string): { linkId: string; secret: string } | null {
  const params = new URLSearchParams(locationHash.replace(/^#/, ""));
  const linkId = params.get("deviceLink") ?? "";
  const secret = params.get("secret") ?? "";
  if (!UUID_PATTERN.test(linkId) || secret.length < 32 || secret.length > 128) return null;
  return { linkId, secret };
}
